import type { AssignmentOut, RoleScopeType, TeamOut } from "@sagot/api-client";
import { useTranslation } from "@sagot/i18n";
import { MultiSelect, RadioGroup } from "@sagot/ui";
import { useId } from "react";
import { useAgents } from "../shell/use-agents";

export interface ScopeValue {
  scope_type: RoleScopeType;
  scope_ids: string[];
}

export const ALL_SCOPE: ScopeValue = { scope_type: "all", scope_ids: [] };

interface ScopePickerProps {
  value: ScopeValue;
  onChange: (value: ScopeValue) => void;
  teams: readonly TeamOut[];
  disabled?: boolean;
}

/** Scope editor for an assignment: all agents, specific agents, or specific teams (§7.3). */
export function ScopePicker({ value, onChange, teams, disabled }: ScopePickerProps) {
  const { t } = useTranslation();
  const agents = useAgents();
  const pickerId = useId();
  const agentsUnavailable = agents.status !== "ready";
  const options = [
    { value: "all" as const, label: t("roles.scope.all"), description: t("roles.scope.allHint") },
    {
      value: "agents" as const,
      label: t("roles.scope.agents"),
      description: agentsUnavailable ? t("roles.scope.agentsUnavailable") : t("roles.scope.agentsHint"),
      disabled: agentsUnavailable,
    },
    { value: "teams" as const, label: t("roles.scope.teams"), description: t("roles.scope.teamsHint"), disabled: teams.length === 0 },
  ];
  const summary = (count: number) => t("roles.scope.selected", { count });
  return (
    <fieldset disabled={disabled} className="space-y-3">
      <legend className="label">{t("roles.scope.title")}</legend>
      <RadioGroup
        aria-label={t("roles.scope.title")}
        variant="cards"
        value={value.scope_type}
        options={options}
        onValueChange={(scope_type) => { onChange({ scope_type, scope_ids: [] }); }}
      />
      {value.scope_type === "agents" ? (
        <div>
          <label className="label" htmlFor={pickerId}>
            {t("roles.scope.chooseAgents")}
          </label>
          <MultiSelect
            id={pickerId}
            options={agents.agents.map((a) => ({ value: a.id, label: a.name }))}
            value={value.scope_ids}
            onValueChange={(scope_ids) => { onChange({ scope_type: "agents", scope_ids }); }}
            labels={{ placeholder: t("roles.scope.chooseAgents"), search: t("roles.scope.searchAgents"), empty: t("roles.scope.noAgents") }}
            summary={summary}
          />
        </div>
      ) : null}
      {value.scope_type === "teams" ? (
        <div>
          <label className="label" htmlFor={pickerId}>
            {t("roles.scope.chooseTeams")}
          </label>
          <MultiSelect
            id={pickerId}
            options={teams.map((team) => ({ value: team.id, label: team.name, description: team.description ?? undefined }))}
            value={value.scope_ids}
            onValueChange={(scope_ids) => { onChange({ scope_type: "teams", scope_ids }); }}
            labels={{ placeholder: t("roles.scope.chooseTeams"), search: t("roles.scope.searchTeams"), empty: t("roles.scope.noTeams") }}
            summary={summary}
          />
          {value.scope_ids.length === 0 ? <p className="mt-1.5 text-xs font-semibold text-warning">{t("roles.scope.pickOne")}</p> : null}
        </div>
      ) : null}
    </fieldset>
  );
}

/** Short label of an assignment's scope ("All agents", "2 teams"…). */
export function useScopeLabel(): (assignment: Pick<AssignmentOut, "scope_type" | "scope_ids">) => string {
  const { t } = useTranslation();
  return (a) => {
    if (a.scope_type === "agents") return t("roles.scope.summaryAgents", { count: a.scope_ids.length });
    if (a.scope_type === "teams") return t("roles.scope.summaryTeams", { count: a.scope_ids.length });
    return t("roles.scope.summaryAll");
  };
}

export function isScopeComplete(scope: ScopeValue): boolean {
  return scope.scope_type === "all" || scope.scope_ids.length > 0;
}
